import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import { isFuture, parseJSON } from 'date-fns';
import classNames from 'classnames';
import StatusIndicator from '../statusIndicator';
import ListWithTooltip from '../listWithTooltip';
import SurveyType from '../surveyType';
import UserAvatar from '../userAvatar';
import ActionButton from '../actionButton';
import DateAndTimeDialog from '../dateAndTimeDialog';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import CustomSelectDialog from '../customSelectDialog';
import AttributesList from '../attributesList';
import Questionnaire from '../questionnaire';
import CustomFormDrawer from '../customFormDrawer';
import {
  SURVEY_GROUP_FIELD,
  getAutoReminderFormFields,
  getAutoReminderDisplayItems,
  prepareInitialAutoReminderData,
} from '../../../constants/survey';
import { SURVEY_STATUS_TYPES } from '../../../constants/statuses';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { SHARE_REPORT_FORM_FIELDS } from '../../../constants/surveySummary';
import { getItemById, isUserDeactivated } from '../../../utility/helpers';
import { formatDate } from '../../../utility/dateUtils';
import { prepareActiveAttributes } from '../../../utility/attribute';
import { prepareInitialShareReportData } from '../../../utility/surveySummary';
import { preparenpsEnpsAttributes } from '../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    borderBottom: `1px solid ${primary.bluish7}`,
    paddingBottom: spacing(8),
    marginBottom: spacing(5),
  },
  info: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr)) repeat(2, 185px)',
    gridColumnGap: 32,
    gridRowGap: 60,
  },
  label: {
    color: primary.bluish3,
    lineHeight: '20px',
    marginBottom: spacing(2),
  },
  attributesWrapper: {
    marginTop: spacing(8),
  },
  attribute: {
    maxWidth: 350,
  },
  questionnaireWrapper: {
    marginTop: spacing(8),
  },
  userMoreItems: {
    backgroundColor: 'transparent',
    fontSize: 16,
    lineHeight: '20px',
    padding: spacing(0, 2),
    marginTop: spacing(0.5),
    maxWidth: 'fit-content',
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    height: 22,
  },
  contentWrapperLarge: {
    height: 24,
  },
  autoRemindersWrapper: {
    gridColumn: '1 / -1',
  },
  autoRemindersContent: {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: '100%',
    height: 'auto',
    minHeight: 24,
  },
  autoReminderFirstRow: {
    display: 'flex',
    alignItems: 'center',
  },
  autoReminderItem: {
    lineHeight: '30px',
    fontFamily: 'ProximaNova-Regular',
  },
  autoReminderLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  autoReminderEditButton: {
    marginLeft: spacing(1),
    flexShrink: 0,
  },
  editButton: {
    position: 'absolute',
    top: -4,
    right: 0,
  },
  editButtonGroup: {
    top: -3,
  },
  groupWrapper: {
    marginTop: spacing(6),
    width: '100%',
  },
  groupTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 900,
  },
  groupTitle: {
    width: 'auto',
  },
  editGroupButton: {
    marginLeft: spacing(1),
  },
  drawerContent: {
    height: '100%',
  },
  autoReminderCheckbox: {
    paddingTop: 0,
  },
}));

const VISIBLE_ITEMS_COUNT = 3;

const SurveyInfo = ({
  translations,
  survey,
  surveyGroups = [],
  statuses,
  types,
  expiryFields,
  organizationUser,
  canManageAllSurveys = false,
  canManageSurveysCreated = false,
  shouldShowGroup = false,
  isPartOfCycle,
  canAccessUserProfileCheck,
  onGoToPersonProfile,
  onEditExpiryDate,
  onChangeGroup,
  onAutoShareReport,
  onEditAutoReminder,
  allUsers,
}) => {
  const classes = useStyles();
  const CYCLE_DATE_FORMAT = 'dd MMM y, hh:mm a';
  const SURVEY_DATE_FORMAT = 'dd MMM y';

  const [isEditExpiryOpened, setEditExpiryOpened] = useState(false);
  const [isManageGroupOpened, setIsManageGroupOpened] = useState(false);
  const [isEditAutoShareOpened, setIsEditAutoShareOpened] = useState(false);
  const [isEditAutoReminderOpened, setIsEditAutoReminderOpened] =
    useState(false);

  const {
    creator,
    createdFor,
    autoSharedUsers,
    autoReminders,
    sentAt,
    expireAt,
    scheduledFor,
    attributes,
    status,
    type,
    completed,
    group,
    questionnaire,
  } = survey;
  const isScheduled = scheduledFor && isFuture(parseJSON(scheduledFor));
  const currentStatus = getItemById(statuses, status);
  const currentType = getItemById(types, type);
  const isAccesibleUser = canAccessUserProfileCheck(creator);
  const initialExpiryDate = new Date(expireAt);
  const isDraft = currentStatus.id === SURVEY_STATUS_TYPES.DRAFT;
  const isCompleted = currentStatus.id === SURVEY_STATUS_TYPES.COMPLETED;
  const isScheduledStatus = currentStatus.id === SURVEY_STATUS_TYPES.SCHEDULED;
  const currentGroup = shouldShowGroup
    ? surveyGroups.find(surveyGroup => surveyGroup.id === group)?.title
    : null;
  const isCreatorActive = !isUserDeactivated(creator);
  const surveyAttributes = preparenpsEnpsAttributes(type, attributes);
  const activeAttributes = isDraft
    ? prepareActiveAttributes(surveyAttributes)
    : surveyAttributes;
  const activeCreatedFor = isDraft
    ? createdFor.filter(user => !isUserDeactivated(user))
    : createdFor;
  const surveyStartDate = isScheduledStatus ? scheduledFor : sentAt;
  const activeAutoReminders = autoReminders || [];
  const autoReminderDisplayItems = getAutoReminderDisplayItems(
    activeAutoReminders,
    surveyStartDate,
    expireAt,
    translations.autoReminderDialog
  );
  const canEditAutoSettings =
    (canManageAllSurveys || canManageSurveysCreated) &&
    !isPartOfCycle &&
    !isCompleted;
  const canEditAutoReminder = canEditAutoSettings && !isDraft;
  const startLabel = useMemo(() => {
    if (isScheduled) {
      return translations.scheduledFor;
    }
    if (isPartOfCycle) {
      return translations.startsOn;
    }
    return translations.dateSent;
  }, [translations, isPartOfCycle, isScheduled]);
  const endLabel = useMemo(() => {
    if (isPartOfCycle) {
      return translations.endsOn;
    }
    return translations.expireAt;
  }, [translations, isPartOfCycle]);

  const toggleEditExpiryDate = () =>
    setEditExpiryOpened(prevOpened => !prevOpened);

  const toggleEditAutoShare = () =>
    setIsEditAutoShareOpened(prevOpened => !prevOpened);

  const toggleEditAutoReminder = () =>
    setIsEditAutoReminderOpened(prevOpened => !prevOpened);

  const handleEditExpiryDate = newDate => {
    onEditExpiryDate(newDate);
    toggleEditExpiryDate();
  };

  const toggleManageGroup = () => setIsManageGroupOpened(prev => !prev);

  const renderLabel = label => (
    <Typography className={classes.label} variant="body2">
      {label}
    </Typography>
  );

  const renderStatus = label => (
    <div>
      {renderLabel(label)}
      <StatusIndicator statuses={statuses} status={currentStatus} />
    </div>
  );

  const renderDate = (label, date, isExpiryEdit = false) => {
    return (
      <div>
        {renderLabel(label)}
        <div className={classes.contentWrapper}>
          <Typography variant="subtitle1">
            {date
              ? formatDate(
                  date,
                  isPartOfCycle ? CYCLE_DATE_FORMAT : SURVEY_DATE_FORMAT
                )
              : '-'}
          </Typography>
          {(canManageAllSurveys || canManageSurveysCreated) &&
            !isPartOfCycle &&
            isExpiryEdit &&
            !isDraft && (
              <ActionButton
                className={classes.editButton}
                type={ACTION_BUTTON_TYPES.EDIT}
                onClickHandler={toggleEditExpiryDate}
              />
            )}
        </div>
      </div>
    );
  };

  const renderCreator = () => (
    <div>
      {renderLabel(translations.createdBy)}
      {isCreatorActive ? (
        <UserAvatar
          variant="subtitle1"
          user={creator}
          clickableCaption={isAccesibleUser}
          onClickHandler={() => onGoToPersonProfile(creator.id)}
          small
          caption
        />
      ) : (
        <Typography variant="subtitle1">-</Typography>
      )}
    </div>
  );

  const renderUsersToReview = () => (
    <div>
      <ListWithTooltip
        labelClass={classes.label}
        moreItemsClass={classes.userMoreItems}
        label={translations.reviewedUsers}
        items={activeCreatedFor}
        isItemClickable={canAccessUserProfileCheck}
        onItemClick={onGoToPersonProfile}
        asyncListProps={{
          position: 'top',
        }}
        organizationUser={organizationUser}
        isUserList
      />
    </div>
  );

  const renderUsersToAutoShare = () => (
    <div>
      {renderLabel(translations.autoSharedUsers)}
      <div
        className={classNames(
          classes.contentWrapper,
          classes.contentWrapperLarge
        )}
      >
        <ListWithTooltip
          moreItemsClass={classes.userMoreItems}
          items={autoSharedUsers}
          isItemClickable={canAccessUserProfileCheck}
          onItemClick={onGoToPersonProfile}
          asyncListProps={{
            position: 'top',
          }}
          organizationUser={organizationUser}
          visibleItemsCount={VISIBLE_ITEMS_COUNT}
          isUserList
        />
        {canEditAutoSettings && (
          <ActionButton
            className={classNames(classes.editButton, classes.editButtonGroup)}
            type={ACTION_BUTTON_TYPES.EDIT}
            onClickHandler={toggleEditAutoShare}
          />
        )}
      </div>
    </div>
  );

  const renderAutoReminderItem = item => (
    <Typography
      key={item.id}
      className={classes.autoReminderItem}
      variant="body1"
      component="p"
    >
      {item.date}{' '}
      <span className={classes.autoReminderLabel}>({item.label})</span>
      {item.hasSuffix ? ',' : ''}
    </Typography>
  );

  const renderAutoReminders = () => {
    const [firstReminder, ...restReminders] = autoReminderDisplayItems;

    return (
      <div className={classes.autoRemindersWrapper}>
        {renderLabel(translations.autoReminders)}
        <div className={classes.autoRemindersContent}>
          <div className={classes.autoReminderFirstRow}>
            {firstReminder ? (
              renderAutoReminderItem(firstReminder)
            ) : (
              <Typography
                className={classes.autoReminderItem}
                variant="body1"
                component="p"
              >
                -
              </Typography>
            )}
            {canEditAutoReminder && (
              <ActionButton
                className={classes.autoReminderEditButton}
                type={ACTION_BUTTON_TYPES.EDIT}
                onClickHandler={toggleEditAutoReminder}
              />
            )}
          </div>
          {restReminders.map(renderAutoReminderItem)}
        </div>
      </div>
    );
  };

  const renderCompletedCounter = () => (
    <div>
      {renderLabel(translations.completed)}
      <div
        className={classNames(
          classes.contentWrapper,
          classes.contentWrapperLarge
        )}
      >
        <Typography variant="subtitle1">{isDraft ? '-' : completed}</Typography>
      </div>
    </div>
  );

  const renderSurveyGroup = () => (
    <div className={classes.groupWrapper}>
      {renderLabel(translations.group)}
      <div className={classes.groupTitleWrapper}>
        <TextBoxWithTooltip
          className={classes.groupTitle}
          variant="subtitle1"
          text={currentGroup || '-'}
        />
        {canManageAllSurveys && !isPartOfCycle && (
          <ActionButton
            className={classes.editGroupButton}
            type={ACTION_BUTTON_TYPES.EDIT}
            onClickHandler={toggleManageGroup}
          />
        )}
      </div>
    </div>
  );

  const renderAttributes = () => (
    <div className={classes.attributesWrapper}>
      {renderLabel(translations.attributes)}
      <AttributesList
        attributeClass={classes.attribute}
        attributes={activeAttributes}
        hasAnswerComment
        isActive
        isHorizontalList
      />
    </div>
  );

  const renderQuestionnaire = () => (
    <div className={classes.questionnaireWrapper}>
      <Questionnaire
        label={translations.questionnaire}
        name={questionnaire?.name}
      />
    </div>
  );

  return (
    <div className={classes.main}>
      <div className={classes.info}>
        {renderStatus(translations.status, status, statuses, types)}
        <SurveyType label={translations.type} type={currentType} hasTooltip />
        {renderDate(startLabel, isScheduled ? scheduledFor : sentAt)}
        {renderDate(endLabel, expireAt, true)}
        {renderCreator()}
        {renderUsersToReview()}
        {renderCompletedCounter()}
        {renderUsersToAutoShare()}
        {renderAutoReminders()}
      </div>
      {questionnaire ? renderQuestionnaire() : renderAttributes()}
      {shouldShowGroup && renderSurveyGroup()}
      <DateAndTimeDialog
        translations={translations.expiryDialog}
        isOpened={isEditExpiryOpened}
        initialDate={initialExpiryDate}
        fields={expiryFields}
        onClose={toggleEditExpiryDate}
        onSave={handleEditExpiryDate}
      />
      {canManageAllSurveys && shouldShowGroup && (
        <CustomSelectDialog
          translations={translations.manageGroupDialog}
          isOpened={isManageGroupOpened}
          options={surveyGroups}
          isEdit={!!group}
          initialValue={group}
          field={SURVEY_GROUP_FIELD}
          onCancel={toggleManageGroup}
          onSave={onChangeGroup}
        />
      )}
      <CustomFormDrawer
        customContentClass={classes.drawerContent}
        translations={translations.autoShareDialog}
        isOpened={isEditAutoShareOpened}
        initialData={prepareInitialShareReportData(autoSharedUsers)}
        fields={SHARE_REPORT_FORM_FIELDS}
        allUsers={allUsers}
        onClose={toggleEditAutoShare}
        onSave={onAutoShareReport}
        hasCancelButton
        hideDelete
        isInitialValid
      />
      <CustomFormDrawer
        customContentClass={classes.drawerContent}
        translations={translations.autoReminderDialog}
        isOpened={isEditAutoReminderOpened}
        initialData={prepareInitialAutoReminderData(
          activeAutoReminders,
          surveyStartDate,
          expireAt
        )}
        fields={getAutoReminderFormFields(
          surveyStartDate,
          expireAt,
          translations.autoReminderDialog,
          classes.autoReminderCheckbox
        )}
        onClose={toggleEditAutoReminder}
        onSave={onEditAutoReminder}
        hasCancelButton
        hideDelete
        isInitialValid
      />
    </div>
  );
};

SurveyInfo.propTypes = {
  translations: PropTypes.object.isRequired,
  survey: PropTypes.shape({}).isRequired,
  surveyGroups: PropTypes.arrayOf(PropTypes.object),
  statuses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  types: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  expiryFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  organizationUser: PropTypes.object.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  isPartOfCycle: PropTypes.bool.isRequired,
  canManageAllSurveys: PropTypes.bool,
  canManageSurveysCreated: PropTypes.bool,
  shouldShowGroup: PropTypes.bool,
  onGoToPersonProfile: PropTypes.func.isRequired,
  onEditExpiryDate: PropTypes.func.isRequired,
  onChangeGroup: PropTypes.func.isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onAutoShareReport: PropTypes.func.isRequired,
  onEditAutoReminder: PropTypes.func.isRequired,
};
export default SurveyInfo;
